import { Component } from '@theme/component';
import { fetchConfig, debounce } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages the cart "gift note" feature (drawer + cart page).
 *
 * Behaviour:
 * - Tick the checkbox            -> reveal the message textarea (NO charge yet).
 * - Type a message              -> add ONE dedicated "Gift Note" fee product (qty 1) AND save the
 *                                   message to the Shopify cart note. The invariant enforced is:
 *
 *                                       fee is in the cart  <=>  (checkbox checked AND note not empty)
 *
 * - Clear the message           -> remove the fee product (nothing to charge for).
 * - Untick the checkbox         -> remove the fee product and clear the note.
 *
 * The message is stored in the Shopify cart note (feeds ShipStation "Notes from Buyer"). Adding or
 * removing the fee re-renders the cart sections, so the order total and cart badge reflect the change.
 *
 * State handling: the checkbox + textarea live inside a `data-skip-subtree-update` wrapper, so the
 * theme's cart re-render (morph) never clobbers their client state — the "checked but not yet typed"
 * state survives re-renders. Fee operations are serialised through a promise chain and each
 * reconciliation re-reads the desired state, so rapid toggling/typing always converges.
 *
 * The Gift Note line has no delete button or quantity control (see cart-products.liquid), so the fee
 * can only be added/removed through this component — there is no external mutation of it to reconcile.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [checkbox] - The "Add a gift note" checkbox.
 * @property {HTMLTextAreaElement} [message] - The gift message textarea.
 *
 * @extends {Component<Refs>}
 */
class GiftNoteComponent extends Component {
  /** Whether the Gift Note fee product is currently in the cart. @type {boolean} */
  #feeInCart = false;

  /** Whether the customer has opened the gift note (checkbox checked). @type {boolean} */
  #open = false;

  /** Serialises fee add/remove/note operations so they can't race. @type {Promise<unknown>} */
  #opChain = Promise.resolve();

  /** In-flight note save, so superseded ones can be aborted. @type {AbortController | null} */
  #activeNoteFetch = null;

  /** True while an add/remove request is in flight. @type {boolean} */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#feeInCart = this.dataset.inCart === 'true';
    this.#open = this.#feeInCart;
  }

  /**
   * The variant id of the Gift Note fee product, read from the theme setting via Liquid.
   * @returns {number | null}
   */
  get variantId() {
    const id = this.dataset.variantId;
    const parsed = id ? Number(id) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  /** @returns {HTMLTextAreaElement | null} */
  get #messageInput() {
    return this.refs.message instanceof HTMLTextAreaElement ? this.refs.message : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #checkboxInput() {
    return this.refs.checkbox instanceof HTMLInputElement ? this.refs.checkbox : null;
  }

  /** @returns {boolean} Whether the message textarea has non-whitespace text. */
  #hasText() {
    return (this.#messageInput?.value ?? '').trim() !== '';
  }

  /**
   * Collect the section ids of every cart-items-component on the page (drawer + cart page),
   * so a single request re-renders all of them (this is what updates the order total + cart badge).
   * @returns {string}
   */
  #cartSectionIds() {
    const ids = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    }
    return Array.from(ids).join(',');
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Checkbox toggle. Bound via `on:change="/onToggle"`.
   * Ticking only reveals the textarea (no charge); unticking removes the fee + clears the note.
   * @param {Event} event
   */
  onToggle = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    // Fail gracefully if the feature is misconfigured.
    if (!this.variantId) {
      checkbox.checked = false;
      return;
    }

    this.#open = checkbox.checked;
    this.#setMessageVisible(this.#open);

    if (this.#open) {
      this.#messageInput?.focus();
      this.#enqueue(() => this.#reconcileFee());
    } else {
      // Clear the field for immediate feedback, then reconcile (removes the fee if present) and
      // guarantee the cart note is cleared even if there was no fee to remove.
      if (this.#messageInput) this.#messageInput.value = '';
      this.#enqueue(async () => {
        await this.#reconcileFee();
        if (!this.#open) await this.#saveNote('');
      });
    }
  };

  /**
   * Message input. Bound via `on:input="/onNoteInput"`. Saves the note and reconciles the fee.
   * Debounced so we don't fire a request on every keystroke.
   */
  onNoteInput = debounce((event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    const value = event.target.value;

    this.#enqueue(async () => {
      await this.#saveNote(value);
      await this.#reconcileFee();
    });
  }, 300);

  // ---------------------------------------------------------------------------
  // Core logic
  // ---------------------------------------------------------------------------

  /**
   * Drives the cart to match the invariant: fee present <=> (open AND has text).
   * Re-reads desired state at call time, so queued reconciliations converge.
   */
  #reconcileFee = async () => {
    const variantId = this.variantId;
    if (!variantId) return;

    const desired = this.#open && this.#hasText();
    if (desired === this.#feeInCart) return;

    this.#setBusy(true);
    try {
      if (desired) {
        await this.#addFee(variantId);
      } else {
        await this.#removeFee(variantId);
      }
    } catch (error) {
      console.error('[gift-note]', error);
      // If we failed to add the fee (e.g. product not purchasable), cancel gracefully so the
      // customer is never left with a note they weren't charged for.
      if (desired) this.#reset();
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * Adds the Gift Note fee product (quantity 1) and re-renders the cart (updates total + badge).
   * @param {number} variantId
   */
  async #addFee(variantId) {
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: this.#cartSectionIds(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    // Shopify returns { status, message, description } on failure.
    if (data.status && data.status !== 200) {
      throw new Error(data.description || data.message || 'Unable to add gift note fee');
    }

    this.#feeInCart = true;
    // /cart/add.js doesn't return item_count, so derive it from the rendered section HTML.
    this.#renderCart(data.sections, this.#itemCountFromSections(data.sections));
  }

  /**
   * Removes every Gift Note fee line and clears the cart note, then re-renders (total + badge).
   * @param {number} variantId
   */
  async #removeFee(variantId) {
    const body = JSON.stringify({
      updates: { [variantId]: 0 },
      note: '',
      sections: this.#cartSectionIds(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const cart = await response.json();

    this.#feeInCart = false;
    this.#renderCart(cart.sections, cart.item_count);
  }

  /**
   * Saves the current message to the Shopify cart note. No `sections` are requested, so this does
   * not re-render the cart (and won't steal focus while typing). Abortable so stale saves are dropped.
   * @param {string} note
   */
  async #saveNote(note) {
    if (this.#activeNoteFetch) this.#activeNoteFetch.abort();
    const abortController = new AbortController();
    this.#activeNoteFetch = abortController;

    try {
      await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ note }) }),
        signal: abortController.signal,
      });
    } catch (error) {
      // Aborted or network error - safe to ignore; a later save will retry.
    } finally {
      if (this.#activeNoteFetch === abortController) this.#activeNoteFetch = null;
    }
  }

  /**
   * Dispatches a cart update so cart-items-component morphs the cart sections and cart-icon updates
   * the header badge. `itemCount` MUST be the real total; the theme's cart-icon reads it directly
   * (and writes it to sessionStorage), so a missing/zero value would wrongly blank the badge.
   * @param {Record<string, string> | undefined} sections
   * @param {number | undefined} itemCount
   */
  #renderCart(sections, itemCount) {
    this.dispatchEvent(
      new CartUpdateEvent({}, 'gift-note-component', {
        source: 'gift-note-component',
        sections,
        itemCount,
      })
    );
  }

  /**
   * Reads the total cart item count from rendered section HTML (the theme renders it in a hidden
   * `[ref="cartItemCount"]` element inside the cart items section).
   * @param {Record<string, string> | undefined} sections
   * @returns {number | undefined}
   */
  #itemCountFromSections(sections) {
    for (const html of Object.values(sections ?? {})) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const text = doc.querySelector('[ref="cartItemCount"]')?.textContent;
      if (text != null && text.trim() !== '') return parseInt(text, 10) || 0;
    }
    return undefined;
  }

  /**
   * Cancels the gift note entirely: untick, hide + empty the textarea, and clear the cart note.
   * Reached when adding the fee failed. Removing the fee is idempotent (setting qty 0 on a variant
   * that isn't in the cart is a harmless server-side no-op), so this also self-heals the rare case
   * where the add succeeded server-side but the response was lost.
   */
  #reset() {
    this.#open = false;

    const checkbox = this.#checkboxInput;
    if (checkbox) checkbox.checked = false;
    if (this.#messageInput) this.#messageInput.value = '';
    this.#setMessageVisible(false);

    this.#enqueue(async () => {
      const variantId = this.variantId;
      if (!variantId) {
        await this.#saveNote('');
        return;
      }
      this.#setBusy(true);
      try {
        await this.#removeFee(variantId);
      } catch (error) {
        console.error('[gift-note]', error);
        await this.#saveNote('');
      } finally {
        this.#setBusy(false);
      }
    });
  }

  /**
   * Shows/hides the message textarea wrapper.
   * @param {boolean} visible
   */
  #setMessageVisible(visible) {
    const wrapper = this.querySelector('.gift-note__message');
    if (wrapper instanceof HTMLElement) wrapper.toggleAttribute('hidden', !visible);
  }

  /**
   * Flags the busy state (disables the checkbox + sets aria-busy) during fee requests.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.setAttribute('aria-busy', busy ? 'true' : 'false');
    const checkbox = this.#checkboxInput;
    if (checkbox) checkbox.disabled = busy;
  }

  /**
   * Adds an operation to the serialised chain so fee add/remove/note saves never overlap.
   * @param {() => Promise<unknown>} fn
   * @returns {Promise<unknown>}
   */
  #enqueue(fn) {
    this.#opChain = this.#opChain.then(fn).catch((error) => console.error('[gift-note]', error));
    return this.#opChain;
  }
}

if (!customElements.get('gift-note-component')) {
  customElements.define('gift-note-component', GiftNoteComponent);
}
